import React, { useState, useMemo, useEffect } from 'react';
import { Quote as QuoteIcon, Heart, Search, Inbox, MessageCircle, ChevronLeft, ChevronRight, ChevronDown, X, Minus, Plus, Type, RefreshCw } from 'lucide-react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../hooks/useFirebase';
import { APP_ID } from '../../constants/config';

// Robust date parser to handle standard YYYY-MM-DD, ISO, and Notion CSV formats
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  // If it strictly matches YYYY-MM-DD, append time to avoid timezone offset shifts
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T12:00:00');
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

const getSortTime = (q) => {
  if (q.date) {
    const d = safeParseDate(q.date);
    if (d) return d.getTime();
  }
  if (q.year) {
    const d = new Date(q.year.toString());
    if (!isNaN(d.getTime())) return d.getTime();
  }
  return 0;
};

const QuotesSection = ({ quotes, theme, isDarkMode, handleNavigate }) => {
  const [localLikes, setLocalLikes] = useState(() => JSON.parse(localStorage.getItem('likedQuotes') || '[]'));
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [quoteFontSize, setQuoteFontSize] = useState(1.25);
  
  // The Zen Hammer: Guaranteeing perfect rounding for pills and cards
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };

  // Dynamically extract available Years, Months, and Tags based on the active filters
  const { availableYears, availableMonths, availableTags, yearCounts, monthCounts, tagCounts } = useMemo(() => {
    const yearCounts = {};
    const monthCounts = {};
    const tagCounts = {};
    let totalYrs = 0;
    let totalMths = 0;
    let totalTgs = 0;
    
    quotes.forEach(q => {
      const dateObj = safeParseDate(q.date);
      const qYear = dateObj ? dateObj.getFullYear().toString() : (q.year?.toString() || '');
      const qMonth = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
      let qTags = [];
      if (q.tags) {
        qTags = Array.isArray(q.tags) ? q.tags : q.tags.split(',').map(s => s.trim());
      }
      
      // Check if the quote matches the *other* active filters
      const matchY = selectedYear === 'All' || qYear === selectedYear;
      const matchM = selectedMonth === 'All' || qMonth === selectedMonth;
      const matchT = selectedTag === 'All' || qTags.includes(selectedTag);

      // Only add to available lists if the other filters aren't blocking this quote
      if (matchM && matchT && qYear) {
        yearCounts[qYear] = (yearCounts[qYear] || 0) + 1;
        totalYrs++;
      }
      if (matchY && matchT && qMonth) {
        monthCounts[qMonth] = (monthCounts[qMonth] || 0) + 1;
        totalMths++;
      }
      if (matchY && matchM) {
        qTags.forEach(t => { if (t) tagCounts[t] = (tagCounts[t] || 0) + 1; });
        totalTgs++;
      }
    });
    
    yearCounts['All'] = totalYrs;
    monthCounts['All'] = totalMths;
    tagCounts['All'] = totalTgs;

    // Sort months chronologically
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const sortedMonths = Object.keys(monthCounts).filter(k => k !== 'All').sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));
    
    return {
      availableYears: Object.keys(yearCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      availableMonths: sortedMonths,
      availableTags: Object.keys(tagCounts).filter(k => k !== 'All').sort(),
      yearCounts,
      monthCounts,
      tagCounts
    };
  }, [quotes, selectedYear, selectedMonth, selectedTag]);

  // Ensure currently selected items stay visible in dropdowns so they can be unselected if they become invalid
  const displayYears = selectedYear !== 'All' && !availableYears.includes(selectedYear) ? [selectedYear, ...availableYears] : availableYears;
  const displayMonths = selectedMonth !== 'All' && !availableMonths.includes(selectedMonth) ? [selectedMonth, ...availableMonths] : availableMonths;
  const displayTags = selectedTag !== 'All' && !availableTags.includes(selectedTag) ? [selectedTag, ...availableTags] : availableTags;

  const filteredQuotes = useMemo(() => {
    let result = quotes;
    
    // 1. Search Query Filter
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(q => 
        (q.text && q.text.toLowerCase().includes(lowerQuery)) ||
        (q.author && q.author.toLowerCase().includes(lowerQuery)) ||
        (q.tags && (Array.isArray(q.tags) ? q.tags.join(' ') : q.tags).toLowerCase().includes(lowerQuery))
      );
    }

    // 2. Category/Time Filters
    if (selectedYear !== 'All' || selectedMonth !== 'All' || selectedTag !== 'All') {
      result = result.filter(q => {
        const dateObj = safeParseDate(q.date);
        const year = dateObj ? dateObj.getFullYear().toString() : (q.year?.toString() || '');
        const month = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
        let qTags = [];
        if (q.tags) {
          qTags = Array.isArray(q.tags) ? q.tags : q.tags.split(',').map(s => s.trim());
        }
        
        const matchYear = selectedYear === 'All' || year === selectedYear;
        const matchMonth = selectedMonth === 'All' || month === selectedMonth;
        const matchTag = selectedTag === 'All' || qTags.includes(selectedTag);

        return matchYear && matchMonth && matchTag;
      });
    }

    // 3. Sort Order
    return [...result].sort((a, b) => sortOrder === 'newest' ? getSortTime(b) - getSortTime(a) : getSortTime(a) - getSortTime(b));
  }, [quotes, selectedYear, selectedMonth, selectedTag, searchQuery, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedTag, searchQuery, sortOrder]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);
  const paginatedQuotes = filteredQuotes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const hasActiveFilters = searchQuery !== '' || sortOrder !== 'newest' || selectedYear !== 'All' || selectedMonth !== 'All' || selectedTag !== 'All';

  const handleResetFilters = () => {
    setSearchQuery('');
    setSortOrder('newest');
    setSelectedYear('All');
    setSelectedMonth('All');
    setSelectedTag('All');
  };

  const handleLike = async (e, quoteId) => {
    e.stopPropagation(); // Prevent the card click from navigating to SingleQuote view
    const isLiked = localLikes.includes(quoteId);
    
    // Optimistic UI update
    const newLikes = isLiked ? localLikes.filter(id => id !== quoteId) : [...localLikes, quoteId];
    setLocalLikes(newLikes);
    localStorage.setItem('likedQuotes', JSON.stringify(newLikes));

    try {
      await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'quotes', quoteId), {
        likes: increment(isLiked ? -1 : 1)
      });
    } catch (error) {
      console.error("Failed to like:", error);
    }
  };

  const handleWhatsAppShare = (e, quote) => {
    e.stopPropagation();
    const url = `${window.location.origin}/?quote=${quote.id}`;
    const shareText = `"${quote.text}" — ${quote.author || 'Ben Hasan'}\n\n${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-1000">
      <div className="max-w-4xl mx-auto w-full">
        
        <style>{`
          @keyframes staggerFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .stagger-item {
            animation: staggerFadeUp 0.6s ease-out both;
          }
        `}</style>
        
        {/* Header Section with Search */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <h2 className={`text-5xl md:text-6xl font-serif ${theme.text}`}>Sanctuary of Thought</h2>
            <p className={`max-w-xl text-sm md:text-base opacity-60 leading-relaxed ${theme.textMuted}`}>
              A curated, chronologically organized collection of profound echoes and epiphanies.
            </p>
          </div>
          
          {/* Search Pill Input */}
          <div 
            style={pillStyle}
            className={`relative flex items-center w-full md:w-64 px-6 py-3 border transition-all duration-500 ${
              isDarkMode 
                ? 'bg-stone-900/40 border-stone-800 focus-within:border-stone-600' 
                : 'bg-stone-50/50 border-stone-100 focus-within:border-stone-300'
            }`}
          >
            <Search size={14} className="opacity-40" />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className={`ml-3 w-full bg-transparent text-xs font-medium outline-none placeholder:opacity-40 ${theme.text}`}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')} 
                className="ml-2 opacity-40 hover:opacity-100"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        
        {/* Interactive Filter Pills */}
        <div className="flex items-center gap-3 overflow-x-auto pb-4 mb-10 no-scrollbar">
          {/* Sort Button */}
          <button 
            onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
            style={pillStyle}
            className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap ${
              isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400'
            }`}
          >
            {sortOrder === 'newest' ? 'Sort: Newest' : 'Sort: Oldest'}
          </button>

          <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

          {/* Year Filter */}
          {displayYears.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsYearOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedYear !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedYear === 'All' ? 'Year' : selectedYear}</span>
                <ChevronDown size={14} className={`${isYearOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isYearOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsYearOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Year</p>
                      {['All', ...displayYears].map(y => (
                        <button key={y} onClick={() => { setSelectedYear(y); setIsYearOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedYear === y ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{y === 'All' ? 'All Years' : y}</span>
                          <span className="opacity-40 text-[10px]">{yearCounts[y] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Month Filter */}
          {displayMonths.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsMonthOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedMonth !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedMonth === 'All' ? 'Month' : selectedMonth}</span>
                <ChevronDown size={14} className={`${isMonthOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isMonthOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsMonthOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Month</p>
                      {['All', ...displayMonths].map(m => (
                        <button key={m} onClick={() => { setSelectedMonth(m); setIsMonthOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedMonth === m ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{m === 'All' ? 'All Months' : m}</span>
                          <span className="opacity-40 text-[10px]">{monthCounts[m] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tag Filter */}
          {displayTags.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsTagOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedTag !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedTag === 'All' ? 'Tag' : selectedTag}</span>
                <ChevronDown size={14} className={`${isTagOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isTagOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsTagOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Tag</p>
                      {['All', ...displayTags].map(t => (
                        <button key={t} onClick={() => { setSelectedTag(t); setIsTagOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedTag === t ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{t === 'All' ? 'All Tags' : t}</span>
                          <span className="opacity-40 text-[10px]">{tagCounts[t] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

          {/* Font Size Controls */}
          <div style={pillStyle} className={`flex items-center px-2 py-1 border flex-shrink-0 transition-colors ${isDarkMode ? 'border-stone-800' : 'border-stone-200'}`}>
            <button onClick={() => setQuoteFontSize(prev => Math.max(1, prev - 0.1))} className={`p-1.5 rounded-full transition-all ${isDarkMode ? 'text-stone-500 hover:bg-stone-800 hover:text-stone-300' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'}`} title="Decrease Font Size"><Minus size={14} /></button>
            <span className={`flex items-center justify-center px-2 opacity-50 ${theme.text}`} title="Font Size"><Type size={12} /></span>
            <button onClick={() => setQuoteFontSize(prev => Math.min(4, prev + 0.1))} className={`p-1.5 rounded-full transition-all ${isDarkMode ? 'text-stone-500 hover:bg-stone-800 hover:text-stone-300' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'}`} title="Increase Font Size"><Plus size={14} /></button>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              style={pillStyle}
              className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                isDarkMode ? 'border-red-900/30 text-red-400 hover:bg-red-900/20' : 'border-red-100 text-red-500 hover:bg-red-50'
              }`}
            >
              <RefreshCw size={14} /> <span>Reset</span>
            </button>
          )}
        </div>
        
        {/* Native CSS Masonry Grid */}
        {paginatedQuotes.length > 0 ? (
          <>
            <div key={`quote-page-${currentPage}`} className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
              {paginatedQuotes.map((quote, index) => (
              <div 
                key={quote.id} 
                onClick={() => handleNavigate('single-quote', null, quote.id)} 
                style={{ 
                  ...cardStyle, 
                  animationDelay: `${index * 100}ms`
                }}
                className={`stagger-item p-8 border transition-colors transition-shadow transition-transform duration-700 cursor-pointer group ${
                  isDarkMode 
                    ? 'bg-stone-900/20 border-stone-800 hover:bg-stone-800/40 hover:border-stone-700' 
                    : 'bg-stone-50/50 border-stone-100 hover:bg-white hover:border-stone-200 shadow-sm hover:shadow-xl hover:-translate-y-1'
                }`}
              >
                <QuoteIcon size={24} className={`mb-6 opacity-20 transition-all duration-500 group-hover:-translate-y-1 ${theme.text}`} />
                <h3 
                  className={`font-serif italic leading-relaxed mb-6 transition-all duration-300 ${theme.text}`}
                  style={{ fontSize: `${quoteFontSize}rem` }}
                >
                  "{quote.text || 'Wisdom loading...'}"
                </h3>
                <div className="flex flex-col gap-2">
                  <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-60 ${theme.text}`}>— {quote.author || 'Ben Hasan'}</p>
                  {(quote.date || quote.year) && (
                    <p className={`text-[9px] font-bold uppercase tracking-[0.2em] opacity-40 ${theme.text}`}>
                      {quote.date 
                        ? (safeParseDate(quote.date) ? safeParseDate(quote.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : quote.date) 
                        : quote.year}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <p className={`text-[8px] uppercase tracking-widest opacity-30 ${theme.text}`}>{quote.tags ? (Array.isArray(quote.tags) ? quote.tags.join(', ') : quote.tags) : ''}</p>
                    
                    <div className="flex items-center gap-4">
                      {/* WhatsApp Share Button */}
                      <button 
                        onClick={(e) => handleWhatsAppShare(e, quote)}
                        className={`opacity-40 group-hover:opacity-100 hover:text-emerald-500 transition-all duration-300 z-10 ${theme.text}`}
                        title="Share to WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </button>
                      
                      {/* Like Button */}
                      <button 
                        onClick={(e) => handleLike(e, quote.id)}
                        className={`flex items-center gap-1.5 transition-all duration-300 z-10 ${localLikes.includes(quote.id) ? 'opacity-100 text-red-500' : `opacity-40 group-hover:opacity-100 hover:text-red-500 ${theme.text}`}`}
                      >
                        <Heart size={14} className={localLikes.includes(quote.id) ? "fill-red-500 text-red-500" : ""} />
                        <span className="text-[10px] font-bold">{quote.likes || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-stone-100 dark:border-stone-900 w-full">
              <button 
                onClick={() => {
                  setCurrentPage(p => Math.max(1, p - 1));
                }} 
                disabled={currentPage === 1} 
                style={pillStyle}
                className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                  currentPage === 1 
                    ? 'opacity-10 cursor-not-allowed border-transparent' 
                    : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                }`}
              >
                <ChevronLeft size={16} /> <span>Prev</span>
              </button>
              
              <span className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme.text}`}>
                {currentPage} / {totalPages}
              </span>
              
              <button 
                onClick={() => {
                  setCurrentPage(p => Math.min(totalPages, p + 1));
                }} 
                disabled={currentPage === totalPages} 
                style={pillStyle}
                className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                  currentPage === totalPages 
                    ? 'opacity-10 cursor-not-allowed border-transparent' 
                    : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                }`}
              >
                <span>Next</span> <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
        ) : (
          <div className="p-20 text-center opacity-20"><Inbox size={48} className="mx-auto mb-4" /><p className="text-[10px] font-bold uppercase tracking-widest">No entries found for this filter.</p></div>
        )}
      </div>
    </section>
  );
};

export default QuotesSection;